import React, { useState, useEffect } from 'react';
import { Image, Video, Palette, Download, Upload, Eye, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface DesignTask {
  id: string;
  post_title: string;
  client: string;
  post_type: 'image' | 'video' | 'carousel' | 'story';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'revision_needed';
  assigned_to: string;
  due_date: string;
  brand_guidelines: {
    colors: string[];
    fonts: string[];
    logo_url: string;
  };
  content_brief: string;
  dimensions: string[];
  created_at: string;
}

export default function DesignWorkspace() {
  const [tasks, setTasks] = useState<DesignTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<DesignTask | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'in_progress' | 'completed'>('pending');

  useEffect(() => {
    // Mock data
    setTasks([
      {
        id: '1',
        post_title: 'New Product Launch Campaign',
        client: 'TechCorp Solutions',
        post_type: 'carousel',
        priority: 'high',
        status: 'pending',
        assigned_to: 'Sarah Wilson',
        due_date: '2024-03-15T10:00:00Z',
        brand_guidelines: {
          colors: ['#2563eb', '#1d4ed8', '#3b82f6'],
          fonts: ['Inter', 'Roboto'],
          logo_url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center'
        },
        content_brief: 'Create a 3-slide carousel showcasing our new AI-powered analytics dashboard. Focus on key features and benefits.',
        dimensions: ['1080x1080', '1080x1920'],
        created_at: '2024-03-10T09:00:00Z'
      },
      {
        id: '2',
        post_title: 'Behind the Scenes Video',
        client: 'Fashion Forward',
        post_type: 'video',
        priority: 'medium',
        status: 'in_progress',
        assigned_to: 'Alex Chen',
        due_date: '2024-03-14T16:00:00Z',
        brand_guidelines: {
          colors: ['#ec4899', '#be185d', '#f472b6'],
          fonts: ['Playfair Display', 'Montserrat'],
          logo_url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=100&h=100&fit=crop&crop=center'
        },
        content_brief: 'Create a 30-second behind-the-scenes video of our design process. Include text overlays and brand elements.',
        dimensions: ['1080x1920', '1080x1080'],
        created_at: '2024-03-09T14:30:00Z'
      },
      {
        id: '3',
        post_title: 'Weekly Menu Special',
        client: 'Healthy Eats',
        post_type: 'image',
        priority: 'medium',
        status: 'completed',
        assigned_to: 'Maria Garcia',
        due_date: '2024-03-12T12:00:00Z',
        brand_guidelines: {
          colors: ['#10b981', '#059669', '#34d399'],
          fonts: ['Poppins', 'Open Sans'],
          logo_url: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=100&h=100&fit=crop&crop=center'
        },
        content_brief: 'Design an appetizing image featuring this week\'s chef special with pricing and nutritional highlights.',
        dimensions: ['1080x1080'],
        created_at: '2024-03-08T11:15:00Z'
      }
    ]);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'revision_needed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5" />;
      case 'carousel': return <Image className="h-5 w-5" />;
      default: return <Image className="h-5 w-5" />;
    }
  };

  const filteredTasks = tasks.filter(task => {
    switch (activeTab) {
      case 'pending': return task.status === 'pending';
      case 'in_progress': return task.status === 'in_progress';
      case 'completed': return task.status === 'completed' || task.status === 'revision_needed';
      default: return true;
    }
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Design Workspace</h1>
        <p className="text-gray-600">Create and manage visual content for social media posts</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'pending', name: 'Pending Tasks', count: tasks.filter(t => t.status === 'pending').length },
              { id: 'in_progress', name: 'In Progress', count: tasks.filter(t => t.status === 'in_progress').length },
              { id: 'completed', name: 'Completed', count: tasks.filter(t => t.status === 'completed' || t.status === 'revision_needed').length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks List */}
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow ${
                  selectedTask?.id === task.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedTask(task)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                    <div className="flex items-center space-x-2">
                      {getPostTypeIcon(task.post_type)}
                      <h3 className="font-semibold text-gray-900">{task.post_title}</h3>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                  <span>{task.client}</span>
                  <span>•</span>
                  <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Assigned to: {task.assigned_to}</span>
                </div>

                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{task.content_brief}</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Dimensions:</span>
                    {task.dimensions.map((dim, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {dim}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {task.status === 'in_progress' && (
                      <Clock className="h-4 w-4 text-blue-500" />
                    )}
                    {task.status === 'completed' && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                    {task.status === 'revision_needed' && (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Design Panel */}
        <div className="lg:col-span-1">
          {selectedTask ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Design Details</h3>
                <button className="text-blue-600 hover:text-blue-700">
                  <Eye className="h-5 w-5" />
                </button>
              </div>

              {/* Brand Guidelines */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Brand Guidelines</h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Colors</label>
                  <div className="flex space-x-2">
                    {selectedTask.brand_guidelines.colors.map((color, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div
                          className="w-8 h-8 rounded border border-gray-200"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs text-gray-500 mt-1">{color}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fonts</label>
                  <div className="space-y-1">
                    {selectedTask.brand_guidelines.fonts.map((font, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded mr-2">
                        {font}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                  <img
                    src={selectedTask.brand_guidelines.logo_url}
                    alt="Brand logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              </div>

              {/* Design Tools */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Design Tools</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button className="flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                    <Palette className="h-4 w-4 mr-2" />
                    Canva
                  </button>
                  <button className="flex items-center justify-center px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100">
                    <Image className="h-4 w-4 mr-2" />
                    Figma
                  </button>
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Upload Design</h4>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Drop files here or click to upload</p>
                  <input type="file" className="hidden" multiple accept="image/*,video/*" />
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Mark as Complete
                </button>
                <button className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                  Request Revision
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  <Download className="h-4 w-4 mr-2 inline" />
                  Download Assets
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">Select a Design Task</h3>
              <p className="text-gray-600">Choose a task from the list to view design details and tools</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}